import type { SubscriptionsDto } from 'components/Admin/organisms/Subscriptions/types';
import type { ThunkAction } from '@reduxjs/toolkit';
import type { RootState } from 'store/index';
import { SubscriptionsService } from 'store/services/subscriptions.service';
import { generalSliceActions } from 'store/slices/general/config';
import type { StripeCardNumberElement } from '@stripe/stripe-js';
import { usersActions, usersSelectors } from 'store/slices/users/config';
import { stripeServiceInstance } from 'store/services/stripe.service';
import { subscriptionsActions } from './config';
import type {
  CreateSubscriptionRequest,
  SubscriptionPackage,
} from './subscriptions.slice';

export const getAllSubscriptions =
  (
    params: Partial<SubscriptionsDto>,
  ): ThunkAction<void, RootState, unknown, any> =>
  async (dispatch) => {
    try {
      dispatch(subscriptionsActions.setSubscriptionsPendingStatus(true));
      const response = await SubscriptionsService.getAll(params);
      dispatch(subscriptionsActions.setSubscriptions(response));
    } catch (err: any) {
      throw new Error(err);
    } finally {
      dispatch(subscriptionsActions.setSubscriptionsPendingStatus(false));
    }
  };

export const getAllSubscriptionPackages =
  (): ThunkAction<void, RootState, unknown, any> => async (dispatch) => {
    try {
      dispatch(subscriptionsActions.setSubscriptionPackagesPendingStatus(true));
      const response = await SubscriptionsService.getSubscriptionPackages();
      dispatch(subscriptionsActions.setSubscriptionPackages(response));
    } catch (err: any) {
      throw new Error(err);
    } finally {
      dispatch(
        subscriptionsActions.setSubscriptionPackagesPendingStatus(false),
      );
    }
  };

export const updateSubscriptionPackage =
  (
    payload: Partial<SubscriptionPackage>,
  ): ThunkAction<void, RootState, unknown, any> =>
  async (dispatch) => {
    try {
      dispatch(subscriptionsActions.setSubscriptionPackagesPendingStatus(true));
      const { id, price } = payload;
      if (!id) {
        return;
      }

      const response = await SubscriptionsService.updateSubscriptionPackage(
        { price },
        id,
      );
      dispatch(subscriptionsActions.setUpdatedPackage(response));
    } catch (err: any) {
      throw new Error(err);
    } finally {
      dispatch(
        subscriptionsActions.setSubscriptionPackagesPendingStatus(false),
      );
    }
  };

export const createSubscription =
  (
    payload: CreateSubscriptionRequest,
    cardElement: StripeCardNumberElement,
    cb: () => void,
  ): ThunkAction<void, RootState, unknown, any> =>
  async (dispatch, getState) => {
    try {
      const profile = usersSelectors.selectProfile(getState());
      const stripeInstance = stripeServiceInstance.getStripeInstance();
      if (!stripeInstance) {
        return;
      }
      dispatch(subscriptionsActions.setCreationStatus(true));
      const { error, paymentMethod } = await stripeInstance.createPaymentMethod(
        {
          type: 'card',
          card: cardElement,
        },
      );

      if (error?.message) {
        dispatch(
          generalSliceActions.setAppMessage({
            type: 'error',
            message: error.message,
          }),
        );
        return;
      }

      if (paymentMethod?.id) {
        const response = await SubscriptionsService.createSubscription({
          ...payload,
          paymentMethodId: paymentMethod.id,
        });
        dispatch(
          subscriptionsActions.setNewSubscription({
            ...response,
            user: profile,
          }),
        );
        dispatch(usersActions.setProfileSubscription(response));
        cb();
      }
    } catch (err: any) {
      dispatch(
        generalSliceActions.setAppMessage({
          type: 'error',
          message: err.message,
        }),
      );
    } finally {
      dispatch(subscriptionsActions.setCreationStatus(false));
    }
  };

export const getSpecificSubscription =
  (id: string): ThunkAction<void, RootState, unknown, any> =>
  async (dispatch) => {
    try {
      dispatch(subscriptionsActions.setSubscriptionsPendingStatus(true));
      const response = await SubscriptionsService.getSpecificSubscription(id);

      dispatch(
        subscriptionsActions.setCachedSubscription({
          key: response.id,
          item: response,
        }),
      );
    } catch (err: any) {
      dispatch(
        generalSliceActions.setAppMessage({
          type: 'error',
          message: err.message,
        }),
      );
    } finally {
      dispatch(subscriptionsActions.setSubscriptionsPendingStatus(false));
    }
  };
